import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import TypeRadar from "../components/TypeRadar";
import { ArrowLeft, CheckCircle2, Loader2, CircleDashed } from "lucide-react";

/**
 * Coverage matrix admin view — Læreplan V851 + Topic-Attack framework.
 * Read-only. Shows target vs current questions for every subtopic and
 * which question_types are already covered.
 */
export default function Coverage() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [visualGaps, setVisualGaps] = useState(null);
  const [phase, setPhase] = useState(0);   // 0 = all
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/coverage?lang=${lang}`),
      api.get(`/coverage/visual-gaps?lang=${lang}`).catch(() => ({ data: null })),
    ])
      .then(([cov, vg]) => {
        setData(cov.data);
        setVisualGaps(vg.data);
      })
      .finally(() => setLoading(false));
  }, [lang]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-5xl mx-auto p-10 text-center text-gray-500">
          {t("loading")}
        </div>
      </div>
    );
  }

  const topics = phase === 0 ? data.topics : data.topics.filter(t => t.phase === phase);
  const overallTarget = data.topics.reduce((s, t) => s + t.target, 0);
  const overallCurrent = data.topics.reduce((s, t) => s + t.current, 0);
  const overallPct = overallTarget ? Math.round((overallCurrent / overallTarget) * 100) : 0;

  const statusIcon = (status) => {
    if (status === "complete") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (status === "in_progress") return <Loader2 className="w-4 h-4 text-amber-500" />;
    return <CircleDashed className="w-4 h-4 text-gray-400" />;
  };
  const statusLabel = (status) =>
    status === "complete" ? t("coverageStatusComplete")
    : status === "in_progress" ? t("coverageStatusInProgress")
    : t("coverageStatusMissing");

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-fjord mb-4">
          <ArrowLeft className="w-4 h-4 me-1.5 rtl:rotate-180" />
          {t("coverageBackToDashboard")}
        </Link>

        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {t("coverageTitle")}
          </h1>
          <p className="text-gray-600 max-w-3xl">{t("coverageSubtitle")}</p>
        </div>

        {/* Summary */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Card className="p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              {t("coverageTotalQuestions")}
            </div>
            <div className="text-3xl font-heading font-bold text-gray-900" data-testid="coverage-total">
              {data.total_questions}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              {t("coverageEnrichedQuestions")}
            </div>
            <div className="text-3xl font-heading font-bold text-fjord" data-testid="coverage-enriched">
              {data.enriched_count}
              <span className="text-base text-gray-400 ms-2">/ {data.total_questions}</span>
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              Phase 1–4 progress
            </div>
            <div className="text-3xl font-heading font-bold text-gray-900" data-testid="coverage-progress-pct">
              {overallPct}%
            </div>
            <Progress value={overallPct} className="h-2 mt-2" />
            <div className="text-xs text-gray-500 mt-1">{overallCurrent} / {overallTarget}</div>
          </Card>
        </div>

        {/* Phase filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="coverage-phase-filter">
          {[0, 1, 2, 3, 4].map(p => (
            <Button
              key={p}
              variant={phase === p ? "default" : "outline"}
              size="sm"
              onClick={() => setPhase(p)}
              data-testid={`coverage-phase-${p}`}
              className={phase === p ? "bg-fjord hover:bg-fjord-700" : ""}
            >
              {p === 0
                ? t("phaseAll")
                : p === 1 ? t("phaseOne")
                : p === 2 ? t("phaseTwo")
                : p === 3 ? t("phaseThree")
                : t("phaseFour")}
            </Button>
          ))}
        </div>

        {/* Topic tables */}
        <div className="space-y-6">
          {topics.map(topic => (
            <Card key={topic.id} className="p-5 bg-white" data-testid={`coverage-topic-${topic.id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] uppercase">Phase {topic.phase}</Badge>
                    <Badge variant="outline" className="text-[10px] uppercase">{topic.trinn.replace("trinn_", "Trinn ")}</Badge>
                  </div>
                  <h2 className="font-heading text-lg font-semibold text-gray-900">{topic.title}</h2>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">{topic.current} / {topic.target}</div>
                  <Progress
                    value={topic.target ? Math.round((topic.current / topic.target) * 100) : 0}
                    className="h-2 w-32"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      <th className="py-2 pe-3 font-medium">{t("coverageColTopic")}</th>
                      <th className="py-2 px-3 font-medium hidden md:table-cell">{t("coverageColRule")}</th>
                      <th className="py-2 px-3 font-medium text-right">{t("coverageColCurrent")} / {t("coverageColTarget")}</th>
                      <th className="py-2 px-3 font-medium hidden lg:table-cell">{t("coverageColTypes")}</th>
                      <th className="py-2 ps-3 font-medium">{t("coverageColStatus")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topic.subtopics.map(s => (
                      <tr key={s.id} className="border-b border-gray-50 last:border-0" data-testid={`coverage-row-${s.id}`}>
                        <td className="py-3 pe-3">
                          <div className="font-medium text-gray-900">{s.title}</div>
                          <div className="flex gap-1 mt-1">
                            {s.visual_required && (
                              <Badge variant="outline" className="text-[10px]">Visual</Badge>
                            )}
                            {s.nvdb_possible && (
                              <Badge variant="outline" className="text-[10px]">NVDB</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 hidden md:table-cell text-xs text-gray-500 max-w-xs">
                          {s.rule_source}
                        </td>
                        <td className="py-3 px-3 text-right tabular-nums">
                          <span className={s.current >= s.coverage_target ? "text-emerald-600 font-semibold" : "text-gray-700"}>
                            {s.current}
                          </span>
                          <span className="text-gray-400"> / {s.coverage_target}</span>
                          {s.gap > 0 && (
                            <div className="text-[10px] text-rose-500 mt-0.5">−{s.gap}</div>
                          )}
                        </td>
                        <td className="py-3 px-3 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <TypeRadar byType={s.by_type} target={s.coverage_target} size={84} />
                            <div className="flex gap-1 flex-wrap">
                              {Object.entries(s.by_type).map(([type, n]) => (
                                <Badge key={type} variant="secondary" className="text-[10px]">
                                  {type} {n}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 ps-3">
                          <div className="flex items-center gap-1.5 text-xs">
                            {statusIcon(s.status)}
                            <span>{statusLabel(s.status)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>

        {data.untagged_question_ids.length > 0 && (
          <Card className="p-5 mt-6 bg-amber-50 border-amber-200" data-testid="coverage-untagged">
            <div className="font-semibold text-amber-900 mb-1">
              {data.untagged_question_ids.length} legacy questions awaiting subtopic tagging
            </div>
            <div className="text-xs text-amber-800/80 break-all font-mono">
              {data.untagged_question_ids.join(", ")}
            </div>
          </Card>
        )}

        {visualGaps && (
          <Card
            data-testid="coverage-visual-gaps"
            className={`p-5 mt-6 ${
              visualGaps.gap_count === 0
                ? "bg-emerald-50 border-emerald-200"
                : "bg-rose-50 border-rose-200"
            }`}
          >
            <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className={`font-semibold ${visualGaps.gap_count === 0 ? "text-emerald-900" : "text-rose-900"}`}>
                  {visualGaps.gap_count === 0
                    ? `All visual-required questions have an asset (${visualGaps.asset_inventory.iconographic_assets} iconographic assets in library)`
                    : `${visualGaps.gap_count} questions missing required visuals`}
                </div>
                <p className={`text-xs ${visualGaps.gap_count === 0 ? "text-emerald-800/80" : "text-rose-800/80"}`}>
                  Per the Visual Representation spec: signs, road markings, and traffic-light questions must show the visual.
                </p>
              </div>
              <div className="flex gap-1 flex-wrap">
                {Object.entries(visualGaps.asset_inventory.by_type).map(([t, n]) => (
                  <Badge key={t} variant="outline" className="text-[10px]">
                    {t.replace(/_/g, " ")} {n}
                  </Badge>
                ))}
              </div>
            </div>
            {visualGaps.gap_count > 0 && (
              <div className="grid sm:grid-cols-2 gap-2 text-xs">
                {visualGaps.gaps.map(g => (
                  <div
                    key={g.question_id}
                    data-testid={`visual-gap-${g.question_id}`}
                    className="bg-white border border-rose-100 rounded-md px-3 py-2"
                  >
                    <div className="font-mono text-rose-900 mb-0.5">
                      {g.question_id} <span className="text-rose-700">[{g.topic}]</span>
                    </div>
                    <div className="text-rose-800/80 line-clamp-2">{g.question_text}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {data.discovered_subtopics?.length > 0 && (
          <Card className="p-5 mt-6 bg-sky-50 border-sky-200" data-testid="coverage-discovered">
            <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="font-semibold text-sky-900">
                  {data.discovered_subtopics.length} AI-discovered subtopics
                </div>
                <p className="text-xs text-sky-800/80">
                  Sub-topic IDs the AI enrichment chose that aren't in the curated V851 registry yet. Review and promote the useful ones in the next iteration.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
              {data.discovered_subtopics.map(d => (
                <div
                  key={d.id}
                  data-testid={`discovered-${d.id}`}
                  className="bg-white border border-sky-100 rounded-md px-3 py-2 flex items-center justify-between"
                >
                  <span className="font-mono text-sky-900 truncate me-2">{d.id}</span>
                  <Badge variant="outline" className="shrink-0">{d.count}</Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
